import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus } from "lucide-react";
import { PRODUCTS, SIZES, RITUAL_SET } from "../data/content";
import { useCart } from "../context/CartContext";
import { Reveal } from "./Reveal";
import { Tilt3D } from "./Tilt3D";

const ProductDetail = ({ product, onClose }) => {
  const { addItem } = useCart();
  const [main, setMain] = useState(product.images[0]);
  const [size, setSize] = useState("M");
  const [qty, setQty] = useState(1);

  const add = () => {
    addItem({
      lookup_key: product.lookup_key,
      name: product.name,
      price: product.price,
      wash: product.wash,
      designImg: product.card,
      size,
      quantity: qty,
    });
    onClose();
  };

  return (
    <motion.div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-bds-bg/92 backdrop-blur-xl" onClick={onClose} />
      <motion.div
        data-testid="product-detail"
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
        className="relative w-full max-w-6xl max-h-[92vh] overflow-y-auto grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-10 md:gap-16 px-4 md:px-8"
      >
        <button
          onClick={onClose}
          data-testid="detail-close"
          className="absolute top-2 right-2 md:top-4 md:right-4 z-10 w-10 h-10 grid place-items-center text-bds-muted hover:text-bds-fg transition-colors"
        >
          <X size={18} />
        </button>

        <div className="pt-6 md:pt-10">
          <Tilt3D max={7} glare={0.22} className="w-full">
            <div className="overflow-hidden mb-6">
              <img key={main} src={main} alt={product.name} className="w-full h-auto object-contain animate-[fadeIn_0.4s_ease] drop-shadow-[0_40px_80px_rgba(0,0,0,0.6)]" />
            </div>
          </Tilt3D>
          <div className="grid grid-cols-4 gap-3">
            {product.images.map((im, i) => (
              <button
                key={im}
                onClick={() => setMain(im)}
                data-testid={`thumb-${i}`}
                className={`overflow-hidden transition-opacity ${main === im ? "opacity-100 ring-1 ring-bds-accent" : "opacity-50 hover:opacity-100"}`}
              >
                <img src={im} alt="" className="w-full h-20 object-cover object-top" />
              </button>
            ))}
          </div>
        </div>

        <div className="pt-6 md:pt-16 md:pr-6">
          <p className="font-mono-bds text-[11px] uppercase tracking-[0.3em] text-bds-accent mb-3">{product.edition}</p>
          <h3 className="font-serif-display font-light text-4xl md:text-5xl leading-[0.95] mb-3">{product.name}</h3>
          <p className="font-mono-bds text-[10px] uppercase tracking-[0.28em] text-bds-muted mb-8">{product.wash}</p>
          <p className="font-serif-display italic text-lg md:text-xl text-bds-fg/80 mb-8 leading-relaxed max-w-md">&ldquo;{product.quote}&rdquo;</p>
          <p className="font-serif-display text-4xl mb-10">NZ${product.price}</p>

          <div className="mb-8">
            <p className="font-mono-bds text-[10px] uppercase tracking-[0.28em] text-bds-muted mb-4">Size</p>
            <div className="flex flex-wrap gap-1.5">
              {SIZES.map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  data-testid={`size-${s}`}
                  className={`min-w-[3rem] h-11 px-3 font-mono-bds text-xs transition-all ${size === s ? "bg-bds-fg text-bds-bg" : "text-bds-muted hover:text-bds-fg"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 mb-10">
            <div className="flex items-center gap-4 text-bds-muted">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-8 h-8 grid place-items-center hover:text-bds-fg transition-colors" data-testid="qty-minus"><Minus size={14} /></button>
              <span className="w-6 text-center font-mono-bds text-bds-fg" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="w-8 h-8 grid place-items-center hover:text-bds-fg transition-colors" data-testid="qty-plus"><Plus size={14} /></button>
            </div>
            <button
              onClick={add}
              data-testid="add-to-cart-btn"
              className="group/add flex-1 relative inline-flex items-center justify-between gap-3 font-mono-bds text-xs uppercase tracking-[0.3em] h-14 px-6 bg-bds-fg text-bds-bg hover:bg-bds-accent hover:text-bds-cream transition-colors"
            >
              <span>Add — NZ${product.price * qty}</span>
              <span className="transition-transform duration-500 group-hover/add:translate-x-1">&rarr;</span>
            </button>
          </div>

          <p className="font-mono-bds text-[11px] uppercase tracking-[0.15em] text-bds-muted leading-[1.9] pt-6 border-t border-bds-border/40">
            {product.fabric}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

const BundleDetail = ({ onClose }) => {
  const { addItem } = useCart();
  const set = RITUAL_SET;
  const [size, setSize] = useState("M");

  const add = () => {
    addItem({
      lookup_key: set.lookup_key,
      name: set.name,
      price: set.price,
      wash: "Bundle",
      designImg: set.card,
      size,
      quantity: 1,
    });
    onClose();
  };

  return (
    <motion.div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-bds-bg/92 backdrop-blur-xl" onClick={onClose} />
      <motion.div
        data-testid="bundle-detail"
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
        className="relative w-full max-w-6xl max-h-[92vh] overflow-y-auto grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-10 md:gap-16 px-4 md:px-8"
      >
        <button
          onClick={onClose}
          data-testid="bundle-close"
          className="absolute top-2 right-2 md:top-4 md:right-4 z-10 w-10 h-10 grid place-items-center text-bds-muted hover:text-bds-fg transition-colors"
        >
          <X size={18} />
        </button>

        <div className="pt-6 md:pt-16 grid grid-cols-2 gap-3 content-start">
          {set.contents.map((c, i) => (
            <Tilt3D key={c.name} max={8} glare={0.25} className="w-full">
              <div className="relative overflow-hidden">
                <img src={c.img} alt={c.name} className="w-full h-44 md:h-56 object-cover object-top drop-shadow-[0_20px_40px_rgba(0,0,0,0.5)]" />
                <span className="absolute top-3 left-3 font-mono-bds text-[9px] uppercase tracking-[0.25em] text-bds-cream/70 mix-blend-difference">0{i + 1}</span>
                <span className="absolute bottom-3 left-3 font-mono-bds text-[9px] uppercase tracking-[0.2em] text-bds-cream/85 mix-blend-difference">
                  {c.name} · NZ${c.price}
                </span>
              </div>
            </Tilt3D>
          ))}
        </div>

        <div className="pt-6 md:pt-16 md:pr-6">
          <div className="flex items-center gap-4 mb-5">
            <span className="h-px w-8 bg-bds-accent" />
            <p className="font-mono-bds text-[10px] uppercase tracking-[0.35em] text-bds-accent">{set.edition}</p>
          </div>
          <h3 className="font-serif-display font-light text-4xl md:text-5xl leading-[0.95] mb-3">{set.name}</h3>
          <p className="font-mono-bds text-[10px] uppercase tracking-[0.28em] text-bds-muted mb-8">{set.tagline}</p>
          <p className="font-serif-display italic text-lg md:text-xl text-bds-fg/80 mb-8 leading-relaxed max-w-md">&ldquo;{set.quote}&rdquo;</p>

          <div className="flex items-baseline gap-4 mb-10">
            <p className="font-serif-display text-4xl">NZ${set.price}</p>
            <p className="font-mono-bds text-sm text-bds-muted line-through">NZ${set.original}</p>
            <span className="font-mono-bds text-[10px] uppercase tracking-[0.25em] text-bds-accent">Save NZ${set.save}</span>
          </div>

          <div className="mb-10">
            <p className="font-mono-bds text-[10px] uppercase tracking-[0.28em] text-bds-muted mb-4">Apparel Size</p>
            <div className="flex flex-wrap gap-1.5">
              {SIZES.map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  data-testid={`bundle-size-${s}`}
                  className={`min-w-[3rem] h-11 px-3 font-mono-bds text-xs transition-all ${size === s ? "bg-bds-fg text-bds-bg" : "text-bds-muted hover:text-bds-fg"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={add}
            data-testid="bundle-add-to-cart-btn"
            className="group/add w-full relative inline-flex items-center justify-between gap-3 font-mono-bds text-xs uppercase tracking-[0.3em] h-14 px-6 bg-bds-fg text-bds-bg hover:bg-bds-accent hover:text-bds-cream transition-colors mb-8"
          >
            <span>Add the Ritual — NZ${set.price}</span>
            <span className="transition-transform duration-500 group-hover/add:translate-x-1">&rarr;</span>
          </button>

          <p className="font-mono-bds text-[11px] uppercase tracking-[0.15em] text-bds-muted leading-[1.9] pt-6 border-t border-bds-border/40">
            {set.fabric}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Shop = () => {
  const [active, setActive] = useState(null);
  const [bundleOpen, setBundleOpen] = useState(false);

  return (
    <section id="shop" data-testid="shop" className="relative py-28 md:py-44 border-t border-bds-border">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
            <div>
              <span className="font-mono-bds text-xs uppercase tracking-[0.3em] text-bds-accent">Inner Demons — Vol. 1</span>
              <h2 className="font-serif-display font-light text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9] mt-6">
                Wear the <span className="italic">weight</span>
              </h2>
            </div>
            <p className="font-grotesk text-bds-muted max-w-sm leading-relaxed">
              Three signature pieces. Heavyweight, oversized, vintage-washed — each a demon made wearable. A portion of every order funds mental-health support. Prices in NZD.
            </p>
          </div>
        </Reveal>

        <Reveal>
          <div
            data-testid="ritual-set-featured"
            className="relative mb-20 md:mb-28 overflow-hidden grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center"
          >
            <div className="lg:col-span-7 relative">
              <div className="grid grid-cols-2 gap-1">
                {RITUAL_SET.contents.map((c, i) => (
                  <Tilt3D key={c.name} max={9} glare={0.3} className="w-full">
                    <div className={`relative overflow-hidden aspect-[4/5]`}>
                      <img
                        src={c.img}
                        alt={c.name}
                        className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] hover:scale-[1.04]"
                      />
                      <span className="absolute bottom-3 left-3 font-mono-bds text-[9px] uppercase tracking-[0.25em] text-bds-cream/70 mix-blend-difference">
                        0{i + 1}
                      </span>
                    </div>
                  </Tilt3D>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5 lg:pl-4">
              <div className="flex items-center gap-4 mb-6">
                <span className="h-px w-8 bg-bds-accent" />
                <span className="font-mono-bds text-[10px] uppercase tracking-[0.35em] text-bds-accent">The Full Uniform</span>
              </div>
              <h3 className="font-serif-display font-light text-5xl md:text-6xl leading-[0.95] tracking-tight mb-6">{RITUAL_SET.name}</h3>
              <p className="font-grotesk text-bds-muted leading-relaxed max-w-md mb-10">
                {RITUAL_SET.tagline} Hoodie, tee, cap & socks — the complete offering. Includes one complimentary sealed Vault card.
              </p>
              <div className="flex items-baseline gap-4 mb-10">
                <span className="font-serif-display text-4xl">NZ${RITUAL_SET.price}</span>
                <span className="font-mono-bds text-sm text-bds-muted line-through">NZ${RITUAL_SET.original}</span>
                <span className="font-mono-bds text-[10px] uppercase tracking-[0.25em] text-bds-accent">Save NZ${RITUAL_SET.save}</span>
              </div>
              <button
                onClick={() => setBundleOpen(true)}
                data-testid="view-ritual-set"
                className="group/btn relative inline-flex items-center gap-3 font-mono-bds text-xs uppercase tracking-[0.3em] pb-3 border-b border-bds-fg text-bds-fg hover:text-bds-accent hover:border-bds-accent transition-colors"
              >
                <span>Complete the Ritual</span>
                <span className="inline-block transition-transform duration-500 group-hover/btn:translate-x-2">&rarr;</span>
              </button>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-20 md:gap-x-12 md:gap-y-24">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.lookup_key} delay={i * 0.08}>
              <div data-testid={`product-${p.lookup_key}`} className="group">
                <Tilt3D max={11} glare={0.32} className="w-full">
                  <button
                    onClick={() => setActive(p)}
                    data-cursor="art"
                    data-testid={`view-${p.lookup_key}`}
                    className="relative block w-full aspect-[4/5] overflow-hidden text-left"
                  >
                    {/* primary (front-facing) */}
                    <img
                      src={p.card}
                      alt={p.name}
                      className="absolute inset-0 w-full h-full object-cover object-top transition-all duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:opacity-0 group-hover:scale-[1.04] group-hover:-translate-y-1"
                    />
                    {/* alt (hover) */}
                    {p.images?.[1] && (
                      <img
                        src={p.images[1]}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover object-top opacity-0 scale-[1.06] translate-y-2 transition-all duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:opacity-100 group-hover:scale-100 group-hover:translate-y-0"
                      />
                    )}
                    {/* editorial index */}
                    <span className="absolute top-4 left-4 z-10 font-mono-bds text-[10px] tracking-[0.3em] text-bds-fg/70 mix-blend-difference">
                      0{i + 1} / 03
                    </span>
                    {/* hover CTA overlay */}
                    <span className="absolute bottom-4 left-4 right-4 z-10 flex items-center justify-between opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                      <span className="font-mono-bds text-[10px] uppercase tracking-[0.3em] text-bds-cream mix-blend-difference">Quick view</span>
                      <span className="font-mono-bds text-[10px] uppercase tracking-[0.3em] text-bds-cream mix-blend-difference">&rarr;</span>
                    </span>
                  </button>
                </Tilt3D>
                <div className="mt-6 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-serif-display font-light text-2xl md:text-3xl leading-none tracking-tight">{p.name}</h3>
                    <p className="font-mono-bds text-[10px] uppercase tracking-[0.28em] text-bds-muted mt-3">{p.wash}</p>
                  </div>
                  <span className="font-serif-display text-xl md:text-2xl whitespace-nowrap">NZ${p.price}</span>
                </div>
                <button
                  onClick={() => setActive(p)}
                  data-testid={`configure-${p.lookup_key}`}
                  className="group/link mt-4 inline-flex items-center gap-2 font-mono-bds text-[10px] uppercase tracking-[0.3em] pb-1 border-b border-bds-muted text-bds-muted hover:text-bds-fg hover:border-bds-fg transition-colors"
                >
                  <span>Select & Add</span>
                  <span className="transition-transform duration-500 group-hover/link:translate-x-1">&rarr;</span>
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && <ProductDetail product={active} onClose={() => setActive(null)} />}
        {bundleOpen && <BundleDetail onClose={() => setBundleOpen(false)} />}
      </AnimatePresence>
    </section>
  );
};
